import { CenteredScrollPage } from '@/components/shared/CenteredScrollPage';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useResetPassword } from '@/hooks/useAuthMutations';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle, Loader2, Lock } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { Notice } from '@/components/ui/notice';

// Form validation schema
const passwordSchema = z.object({
  newPassword: z
    .string()
    .min(8, 'Password must be at least 8 characters long')
    .max(100, 'Password must be less than 100 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type PasswordFormData = z.infer<typeof passwordSchema>;

export default function PasswordResetConfirm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const resetPasswordMutation = useResetPassword();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    mode: 'onBlur',
  });
  
  const isPending = resetPasswordMutation.isPending;

  if (resetPasswordMutation.isSuccess) {
    return (
      <CenteredScrollPage>
        <Card className="w-full max-w-sm">
          <CardHeader>
            <div className="flex items-center justify-center mb-4">
              <CheckCircle className="h-16 w-16 text-success" />
            </div>
            <CardTitle className="text-center">Password Reset Complete</CardTitle>
            <CardDescription className="text-center">
              Your password has been successfully updated
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Notice tone="success" className="p-4">
                <p className="text-sm text-center">
                  You can now log in with your new password using your registered email address.
                </p>
              </Notice>
              
              <Button 
                className="w-full"
                onClick={() => navigate('/login')}
              >
                Go to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </CenteredScrollPage>
    );
  }

  // Show error if no token
  if (!token) {
    return (
      <CenteredScrollPage>
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle className="text-center text-destructive">Invalid Link</CardTitle>
            <CardDescription className="text-center">
              This password reset link is invalid or has expired.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              className="w-full"
              onClick={() => navigate('/login')}
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </CenteredScrollPage>
    );
  }

  return (
    <CenteredScrollPage>
      <Card className="w-full max-w-sm">
        <CardHeader>
          <div className="flex items-center justify-center mb-4">
            <Lock className="h-12 w-12" />
          </div>
          <CardTitle className="text-center">Set New Password</CardTitle>
          <CardDescription className="text-center">
            Enter your new password below
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form 
          onSubmit={handleSubmit((data) => {
            resetPasswordMutation.mutate({ passwordResetToken: token, newPassword: data.newPassword });
          })} 
          className="space-y-4">
            <div>
              <Input
                type="password"
                placeholder="New password"
                {...register('newPassword')}
                disabled={isPending}
              />
              {errors.newPassword && (
                <p className="text-destructive text-sm mt-1">
                  {errors.newPassword.message}
                </p>
              )}
            </div>
            
            <div>
              <Input
                type="password"
                placeholder="Confirm new password"
                {...register('confirmPassword')}
                disabled={isPending}
              />
              {errors.confirmPassword && (
                <p className="text-destructive text-sm mt-1">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>
            
            <Button 
              type="submit" 
              className="w-full"
              disabled={isPending || isSubmitting}
            >
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Updating Password...
                </>
              ) : (
                'Update Password'
              )}
            </Button>
          </form>

          {resetPasswordMutation.isError && (
            <p className="text-destructive mt-4 text-sm">
              {resetPasswordMutation.error?.message || 'Failed to reset password. Please try again.'}
            </p>
          )}
        </CardContent>
      </Card>
    </CenteredScrollPage>
  );
}

